import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { sheetReader } from 'utils/fuctions/newSheetReader.js';
import { generateSheetHeaders } from 'utils/fuctions/generateSheetHeaders.js';

const initialFileInfo = {
  file: '',
  data: [],
  originalData: [],
  hasHeader: true,
  columns: []
};

/**
 * Hook personalizado para gerenciar importações de arquivos e processar dados de planilhas.
 *
 * @param {number} maxRecords - Número máximo de registros permitidos para importação (padrão: 50000)
 * @returns {Object} Um objeto contendo:
 *  - fileInfo: Objeto contendo o estado atual do arquivo com dados, colunas e metadados
 *  - handleFileChange: Função para processar arquivos carregados
 *  - changeHasHeader: Função para alternar se o arquivo possui linha de cabeçalho
 *  - resetFileInfo: Função para redefinir as informações do arquivo ao estado inicial
 */

export const useFileImport = (maxRecords = 50000) => {
  const [fileInfo, setFileInfo] = useState(initialFileInfo);

  useEffect(() => {
    const [headers] = fileInfo.data;
    if (!headers) {
      setFileInfo((s) => ({ ...s, columns: [] }));
      return;
    }
    const columns = headers.map((e) => ({ col: e }));
    setFileInfo((s) => ({ ...s, columns }));
  }, [fileInfo.data]);

  const handleFileChange = async (acceptedFiles) => {
    if (!acceptedFiles?.length) return;

    try {
      const file = acceptedFiles[0];
      const result = await sheetReader(file);

      const maxLimit = fileInfo.hasHeader ? maxRecords + 1 : maxRecords;
      if (result.length > maxLimit) {
        toast.error(`A importação deve ter no máximo ${maxRecords} registros`);
        return;
      }

      setFileInfo((s) => ({
        ...s,
        data: result,
        originalData: result,
        file,
        hasHeader: true
      }));
    } catch (error) {
      console.log(error);
      toast.error('Erro ao ler arquivo');
    }
  };

  const changeHasHeader = (e) => {
    const hasHeader = e.target.checked;
    setFileInfo((s) => {
      const newState = { ...s, hasHeader };
      if (!hasHeader) {
        const headers = generateSheetHeaders(s.originalData[0]?.length || 0);
        newState.data = [headers, ...s.originalData];
      } else {
        newState.data = [...s.originalData];
      }
      return newState;
    });
  };

  const resetFileInfo = () => {
    setFileInfo(initialFileInfo);
  };

  return {
    fileInfo,
    handleFileChange,
    changeHasHeader,
    resetFileInfo
  };
};
